import * as React from 'react';
import { MembershipItemIndexView, SuccinctCampaign } from '../../../types/membershipPlans';
import { MembershipItemId } from '../../../../commonv3/types/membershipPlans';
import classNames from 'classnames';
import './planOptions.module.scss';
import { observer } from 'mobx-react';
import { StrikeThrough } from '../format/StrikeThrough';
import { LocalOfferOutlined } from '@material-ui/icons';
import { SelectableCard } from '../select/SelectableCard';

type CampaignSummaryProps = {
    campaign: SuccinctCampaign;
};

const CampaignSummary = ({ campaign }: CampaignSummaryProps) => (
    <div styleName="campaign-summary">
        <div styleName="campaign-name">
            <LocalOfferOutlined styleName="campaign-icon" />
            {campaign.name}
        </div>
        <div styleName="campaign-discount">{campaign.discount}円OFF</div>
    </div>
);

export const SimpleItemPriceSummary = ({ price, durationMonth }) => (
    <div styleName="simple-item-price-summary">
        ￥{price.toLocaleString()} / {durationMonth}ヶ月
    </div>
);

export const ItemPriceSummary = ({ price, priceWithTax, discount = 0, durationMonth }) => {
    const discountPrice = Math.max(price - discount, 0);
    const discountPriceWithTax = Math.max(priceWithTax - discount, 0);
    return (
        <div styleName="item-price-summary">
            {durationMonth == 1 && (
                <div styleName="price-wrapper">
                    {price != discountPrice && (
                        <StrikeThrough styleName="original-price" lineColor="gray">
                            <div styleName="price-without-tax">
                                <span styleName="price-number">{price.toLocaleString()}</span>円
                            </div>
                            <div styleName="price-with-tax">（税込 {priceWithTax.toLocaleString()}円）</div>
                        </StrikeThrough>
                    )}
                    <div styleName="price">
                        <div styleName="price-without-tax">
                            <span styleName="price-number">{discountPrice.toLocaleString()}</span>円
                        </div>
                        <div styleName="price-with-tax">（税込 {discountPriceWithTax.toLocaleString()}円）/月</div>
                    </div>
                </div>
            )}
            {durationMonth > 1 && (
                <div>
                    <div styleName="price-wrapper">
                        {price != discountPrice && (
                            <StrikeThrough styleName="original-price" lineColor="gray">
                                <div styleName="price-without-tax">
                                    <span styleName="price-number">{price.toLocaleString()}</span>円
                                </div>
                                <div styleName="price-with-tax">（税込 {priceWithTax.toLocaleString()}円）</div>
                            </StrikeThrough>
                        )}
                        <div styleName="price">
                            <div styleName="price-without-tax">
                                一括<span styleName="price-number">{discountPrice.toLocaleString()}</span>円
                            </div>
                            <div styleName="price-with-tax">（税込 {discountPriceWithTax.toLocaleString()}円）</div>
                        </div>
                    </div>
                    <div styleName="monthly-price">
                        月あたり税込 {Math.round(discountPriceWithTax / durationMonth).toLocaleString()}円
                    </div>
                </div>
            )}
        </div>
    );
};

type ItemProps = {
    id?: string;
    current: boolean;
    selected: boolean;
    basePrice: number;
    item: MembershipItemIndexView;
    onClick: () => any;
};

const Item = ({ id, current, selected, item, basePrice, onClick }: ItemProps) => {
    const benefit = basePrice * item.duration_month - item.total_price;
    const effectiveCampaigns = item.discount_campaigns.filter((d) => d.discount > 0);
    return (
        <SelectableCard
            id={id}
            current={current}
            selected={selected}
            currentLabel="契約中"
            onClick={onClick}
            footer={
                effectiveCampaigns.length > 0 &&
                effectiveCampaigns.map((campaign, idx) => (
                    <CampaignSummary key={`${campaign.name}${idx}`} campaign={campaign} />
                ))
            }
        >
            <div styleName="item-body">
                <div styleName="item-left">
                    <div styleName="item-name">{item.duration_month}ヶ月プラン</div>
                    {benefit > 0 && (
                        <div styleName={classNames('item-benefit', `duration-${item.duration_month}`)}>
                            {benefit.toLocaleString()}円(税込)<br styleName="br-sp" />お得！
                        </div>
                    )}
                </div>
                <div styleName="item-right">
                    <ItemPriceSummary
                        durationMonth={item.duration_month}
                        price={item.price}
                        priceWithTax={item.price_with_tax}
                        discount={item.discount}
                    />
                </div>
            </div>
        </SelectableCard>
    );
};

type SelectorProps = {
    currentItemId?: MembershipItemId;
    selectedItemId: MembershipItemId;
    items: MembershipItemIndexView[];
    onItemSelect: (itemId: MembershipItemId) => any;
};

export const PlanOptionSelector = observer(({ currentItemId, selectedItemId, items, onItemSelect }: SelectorProps) => {
    if (items.length === 0) {
        return null;
    }
    const sorted = items.sort((x, y) => y.duration_month - x.duration_month);
    const baseItem = sorted[sorted.length - 1];
    const basePrice = Math.round(baseItem.price_with_tax / baseItem.duration_month);
    return (
        <div styleName="option-selector">
            {sorted.map((item) => (
                <Item
                    id={`option-${item.duration_month}-months`}
                    key={item.id}
                    current={item.id === currentItemId}
                    selected={item.id === selectedItemId}
                    basePrice={basePrice}
                    item={item}
                    onClick={() => onItemSelect(item.id)}
                />
            ))}
        </div>
    );
});

export const PlanNotice = ({ children }) => <div styleName="plan-notice">{children}</div>;
